import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavigationBar from "components/Navigation";
import { memberService } from "services/memberService";
import { Building } from "types";
import {
  MapContainer,
  MapHeader,
  MapTitle,
  GuideMessage,
  KakaoMapWrapper,
  InfoOverlay,
  InfoContent,
  BuildingImage,
  BuildingInfo,
  InfoTitle,
  InfoDetails,
  InfoItem,
  ButtonWrapper,
  InfoButton
} from "./Map.styles";

declare global {
  interface Window {
    kakao: any;
  }
}

const Map: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);

  // 건물 데이터 가져오기
  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const data = await memberService.getMyBuildings();
        setBuildings(data);
      } catch (error) {
        console.error('Failed to fetch buildings:', error);
      }
    };

    fetchBuildings();
  }, []);

  // 카카오맵 초기화 및 마커 표시
  useEffect(() => {
    if (!mapRef.current || buildings.length === 0) return;

    const script = document.createElement("script");
    script.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=71f900d9b14c079c34329764a15e7ae2&autoload=false`;
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      window.kakao.maps.load(() => {
        const container = mapRef.current;
        // 첫 번째 건물 좌표를 중심으로 설정
        const centerLat = buildings[0]?.latitude || 35.1426;
        const centerLng = buildings[0]?.longitude || 129.0352;

        const options = {
          center: new window.kakao.maps.LatLng(centerLat, centerLng),
          level: 3,
        };

        const map = new window.kakao.maps.Map(container, options);

        // 지도 이동 범위 제한 설정
        // 모든 건물 좌표를 포함하는 범위 계산
        const latitudes = buildings.map(b => b.latitude);
        const longitudes = buildings.map(b => b.longitude);

        const minLat = Math.min(...latitudes) - 0.01; // 여유 공간 추가
        const maxLat = Math.max(...latitudes) + 0.01;
        const minLng = Math.min(...longitudes) - 0.01;
        const maxLng = Math.max(...longitudes) + 0.01;


        map.setMaxLevel(5); // 최대 줌 아웃 레벨 제한
        map.setMinLevel(1); // 최대 줌 인 레벨 제한

        // 지도 이동 범위 제한 이벤트
        window.kakao.maps.event.addListener(map, 'dragend', function() {
          const center = map.getCenter();
          const lat = center.getLat();
          const lng = center.getLng();

          let newLat = lat;
          let newLng = lng;

          if (lat < minLat) newLat = minLat;
          if (lat > maxLat) newLat = maxLat;
          if (lng < minLng) newLng = minLng;
          if (lng > maxLng) newLng = maxLng;

          if (newLat !== lat || newLng !== lng) {
            map.setCenter(new window.kakao.maps.LatLng(newLat, newLng));
          }
        });

        // 커스텀 마커 추가
        buildings.forEach((building) => {
          const markerPosition = new window.kakao.maps.LatLng(building.latitude, building.longitude);

          // 커스텀 마커 HTML 생성 (핀 모양)
          const content = document.createElement('div');
          content.className = 'custom-marker';
          content.innerHTML = `
            <div style="position: relative; width: 40px; height: 50px; cursor: pointer;">
              <svg width="40" height="50" viewBox="0 0 40 50" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
                <path d="M20 0C11.716 0 5 6.716 5 15c0 8.284 15 35 15 35s15-26.716 15-35c0-8.284-6.716-15-15-15z"
                      fill="#4CAF50"
                      stroke="#fff"
                      stroke-width="1"/>
                <circle cx="20" cy="15" r="10" fill="#fff"/>
              </svg>
              <div style="
                position: absolute;
                top: 7px;
                left: 50%;
                transform: translateX(-50%);
                color: #4CAF50;
                font-weight: bold;
                font-size: 12px;
                text-align: center;
                width: 20px;
                pointer-events: none;
              ">
                ${building.label || building.name.substring(0, 2)}
              </div>
            </div>
          `;

          // CustomOverlay 생성
          const overlay = new window.kakao.maps.CustomOverlay({
            position: markerPosition,
            content: content,
            yAnchor: 1
          });

          overlay.setMap(map);

          // 마커 클릭 이벤트
          content.addEventListener('click', () => {
            setSelectedBuilding(building);
            map.panTo(markerPosition);
          });
        });
      });
    };

    return () => {
      document.head.removeChild(script);
    };
  }, [buildings]);

  const handleDetailClick = () => {
    if (selectedBuilding) {
      navigate(`/buildings/${selectedBuilding.id}`);
    }
  };

  return (
    <>
      <MapContainer>
        <MapHeader>
          <MapTitle>캠퍼스맵</MapTitle>
        </MapHeader>
        <GuideMessage>
          마커 클릭 시 건물 정보를 확인 할 수 있습니다.
        </GuideMessage>
        <KakaoMapWrapper ref={mapRef} />

        {selectedBuilding && (
          <InfoOverlay>
            <InfoContent>
              <BuildingImage
                src={selectedBuilding.thumbnailImage}
                alt={selectedBuilding.name}
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/100x120?text=No+Image';
                }}
              />
              <BuildingInfo>
                <InfoTitle>{selectedBuilding.name}</InfoTitle>
                <InfoDetails>
                  {selectedBuilding.operationTimes && selectedBuilding.operationTimes.length > 0 ? (
                    selectedBuilding.operationTimes.map((time, index) => (
                      <InfoItem key={index}>
                        {time.name}: {time.startTime.substring(0, 5)} ~ {time.endTime.substring(0, 5)}
                      </InfoItem>
                    ))
                  ) : (
                    <>
                      {selectedBuilding.location && (
                        <InfoItem>위치: {selectedBuilding.location}</InfoItem>
                      )}
                      {selectedBuilding.description && (
                        <InfoItem>{selectedBuilding.description}</InfoItem>
                      )}
                    </>
                  )}
                </InfoDetails>
                <ButtonWrapper>
                  <InfoButton onClick={handleDetailClick}>
                    상세정보
                  </InfoButton>
                </ButtonWrapper>
              </BuildingInfo>
            </InfoContent>
          </InfoOverlay>
        )}
      </MapContainer>
      <NavigationBar />
    </>
  );
};

export default Map;
